/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { StatusPill } from '../../shared/surface-cards/StatusPill';
import { CatalogPagination } from '../../shared/ui/CatalogPagination';
import { ModalStatePanel } from '../../shared/modals/ModalStatePanel'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { OperationalFilterChipGroup } from '../../shared/filters/OperationalFilterPrimitives';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography'
import { ticketColors } from '../../shared/theme/ticketColors';
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { formatLocalDate } from '@shared/vueltoDateTime';

export type CustomerPendingSalesPageSize = 5 | 10;
const DEFAULT_PAGE_SIZE: CustomerPendingSalesPageSize = 5;

export type CustomerPendingSalesFilter = 'all' | 'unpaid' | 'partial';

const FILTER_OPTIONS: Array<{ value: CustomerPendingSalesFilter; label: string }> = [
  { value: 'all', label: 'Todas' },
  { value: 'unpaid', label: 'Sin cobro' },
  { value: 'partial', label: 'Parcial' },
];

export type CustomerPendingSaleRow = {
  id: string;
  shortId: string;
  createdAt: string;
  total: number;
  paidAmount: number;
  pendingAmount: number;
};

type Props = {
  /** Reinicia la página al cambiar de cliente. */
  customerId: string;
  sales: CustomerPendingSaleRow[];
  loading: boolean;
  formatMoney: (n: number) => string;
  onPressSale?: (saleId: string) => void;
  onCollectSale?: (sale: CustomerPendingSaleRow) => void;
};

function saleStatus(sale: CustomerPendingSaleRow): 'paid' | 'partial' | 'fiado' {
  if (sale.pendingAmount <= 0) return 'paid';
  if (sale.paidAmount > 0) return 'partial';
  return 'fiado';
}

export function CustomerPendingSalesPanel({
  customerId,
  sales,
  loading,
  formatMoney,
  onPressSale,
  onCollectSale,
}: Props) {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState<CustomerPendingSalesPageSize>(DEFAULT_PAGE_SIZE);
  const [statusFilter, setStatusFilter] = useState<CustomerPendingSalesFilter>('all');

  useEffect(() => {
    setPageIndex(0);
    setStatusFilter('all');
  }, [customerId]);

  useEffect(() => {
    setPageIndex(0);
  }, [statusFilter]);

  const filteredSales = useMemo(() => {
    if (statusFilter === 'all') return sales;
    if (statusFilter === 'partial') {
      return sales.filter((sale) => saleStatus(sale) === 'partial');
    }
    return sales.filter((sale) => saleStatus(sale) === 'fiado');
  }, [sales, statusFilter]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredSales.length / pageSize) - 1);
    if (pageIndex > maxPage) {
      setPageIndex(maxPage);
    }
  }, [filteredSales.length, pageSize, pageIndex]);

  const totalPending = useMemo(
    () => sales.reduce((sum, sale) => sum + Math.max(0, sale.pendingAmount), 0),
    [sales]
  );

  const filteredPending = useMemo(
    () => filteredSales.reduce((sum, sale) => sum + Math.max(0, sale.pendingAmount), 0),
    [filteredSales]
  );

  const pageSales = useMemo(() => {
    const start = pageIndex * pageSize;
    return filteredSales.slice(start, start + pageSize);
  }, [filteredSales, pageIndex, pageSize]);

  const hasMore = (pageIndex + 1) * pageSize < filteredSales.length;

  const loadPreviousPage = useCallback(() => {
    setPageIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const loadNextPage = useCallback(() => {
    setPageIndex((prev) => prev + 1);
  }, []);

  const handlePageSizeChange = useCallback((size: CustomerPendingSalesPageSize) => {
    setPageSize(size);
    setPageIndex(0);
  }, []);

  const tappable = Boolean(onPressSale);
  const showCollectActions = Boolean(onCollectSale);

  if (loading) {
    return (
      <View style={styles.section}>
        <ModalStatePanel loading loadingMessage="Cargando ventas…" />
      </View>
    );
  }

  if (sales.length === 0) {
    return (
      <View style={styles.section}>
        <ModalStatePanel
          empty
          emptyTitle="Sin ventas pendientes"
          emptyMessage="Este cliente no tiene ventas a cuenta pendientes."
        />
      </View>
    );
  }

  if (filteredSales.length === 0) {
    return (
      <View style={styles.section}>
        <View style={styles.sectionHeading}>
          <Text style={modalExperience.formSectionTitle}>Ventas pendientes de cobro</Text>
          <View style={styles.sectionSummary}>
            <Text style={styles.sectionSummaryText}>
              {sales.length} {sales.length === 1 ? 'venta' : 'ventas'}
            </Text>
            <Text style={styles.sectionSummaryDot}>·</Text>
            <Text style={styles.sectionSummaryAmount}>{formatMoney(totalPending)}</Text>
          </View>
        </View>
        <View style={styles.filterPanel}>
          <OperationalFilterChipGroup
            options={FILTER_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
          />
        </View>
        <ModalStatePanel
          empty
          emptyTitle="Sin resultados"
          emptyMessage="Ninguna venta coincide con el filtro elegido."
        />
      </View>
    );
  }

  const listHeader = (
    <View style={styles.tableHeaderRow} pointerEvents="none">
      <Text style={[styles.tableHeaderCell, styles.colDate]}>Fecha</Text>
      <Text style={[styles.tableHeaderCell, styles.colTicket]}>Ticket</Text>
      <Text style={[styles.tableHeaderCell, styles.colAmount]}>Pendiente</Text>
      {showCollectActions ? <View style={styles.colCollect} /> : null}
      {tappable ? <View style={styles.colChevron} /> : null}
    </View>
  );

  return (
    <View style={styles.section}>
      <View style={styles.sectionHeading}>
        <Text style={modalExperience.formSectionTitle}>Ventas pendientes de cobro</Text>
        <View style={styles.sectionSummary}>
          <Text style={styles.sectionSummaryText}>
            {statusFilter === 'all'
              ? `${sales.length} ${sales.length === 1 ? 'venta' : 'ventas'}`
              : `${filteredSales.length} de ${sales.length} ventas`}
          </Text>
          <Text style={styles.sectionSummaryDot}>·</Text>
          <Text style={styles.sectionSummaryAmount}>
            {statusFilter === 'all' ? formatMoney(totalPending) : formatMoney(filteredPending)}
          </Text>
        </View>
      </View>

      <View style={styles.filterPanel}>
        <OperationalFilterChipGroup
          options={FILTER_OPTIONS}
          value={statusFilter}
          onChange={setStatusFilter}
        />
      </View>

      <View style={modalExperience.listContainer}>
        {listHeader}
        {pageSales.map((sale, index) => {
          const status = saleStatus(sale);
          const progress = sale.total > 0 ? (sale.paidAmount / sale.total) * 100 : 0;
          const isLast = index === pageSales.length - 1;
          const canCollectSale = showCollectActions && sale.pendingAmount > 0;

          const row = (
            <View style={[styles.dataRow, isLast && styles.dataRowLast]}>
              <TouchableOpacity
                style={styles.rowMainPressable}
                disabled={!tappable}
                accessibilityRole={tappable ? 'button' : undefined}
                accessibilityLabel={
                  tappable
                    ? `Ver venta ${sale.shortId}, pendiente ${formatMoney(sale.pendingAmount)}`
                    : undefined
                }
                onPress={tappable ? () => onPressSale?.(sale.id) : undefined}
                activeOpacity={tappable ? 0.7 : 1}
              >
                <Text style={[styles.cellDate, styles.colDate]} numberOfLines={1}>
                  {formatLocalDate(sale.createdAt)}
                </Text>
                <View style={[styles.colTicket, styles.ticketCell]}>
                  <Text style={styles.ticketId} numberOfLines={1}>
                    #{sale.shortId}
                  </Text>
                  {status === 'partial' ? (
                    <Text style={styles.ticketMeta} numberOfLines={1}>
                      {formatMoney(sale.paidAmount)} de {formatMoney(sale.total)}
                    </Text>
                  ) : (
                    <Text style={styles.ticketMeta} numberOfLines={1}>
                      Total {formatMoney(sale.total)}
                    </Text>
                  )}
                </View>
                <View style={[styles.colAmount, styles.amountCell]}>
                  {status === 'partial' ? (
                    <StatusPill label="Parcial" tone="warning" compact />
                  ) : null}
                  <Text
                    style={[styles.pendingAmount, status === 'paid' && styles.pendingAmountPaid]}
                    numberOfLines={1}
                  >
                    {formatMoney(sale.pendingAmount)}
                  </Text>
                </View>
                {tappable ? (
                  <View style={styles.colChevron}>
                    <Ionicons name="chevron-forward" size={16} color={ticketColors.ink3} />
                  </View>
                ) : null}
              </TouchableOpacity>
              {canCollectSale ? (
                <TouchableOpacity
                  style={styles.collectBtn}
                  onPress={() => onCollectSale?.(sale)}
                  accessibilityRole="button"
                  accessibilityLabel={`Cobrar venta ${sale.shortId}, pendiente ${formatMoney(sale.pendingAmount)}`}
                >
                  <Text style={styles.collectBtnText}>Cobrar</Text>
                </TouchableOpacity>
              ) : showCollectActions ? (
                <View style={styles.colCollect} />
              ) : null}
              {status === 'partial' ? (
                <View style={styles.progressTrack}>
                  <View style={[styles.progressFill, { width: `${progress}%` }]} />
                </View>
              ) : null}
            </View>
          );

          return <React.Fragment key={sale.id}>{row}</React.Fragment>;
        })}
      </View>

      <CatalogPagination
        catalogPageIndex={pageIndex}
        pageSize={pageSize}
        catalogTotalCount={filteredSales.length}
        hasMore={hasMore}
        productsLoading={false}
        productCount={pageSales.length}
        onPreviousPage={loadPreviousPage}
        onNextPage={loadNextPage}
        listNouns={{ singular: 'venta', plural: 'ventas' }}
        alwaysShowPagination={filteredSales.length > pageSize}
        pageSizePicker={{
          label: 'Ventas por página',
          options: [5, 10] as const,
          value: pageSize,
          onChange: handlePageSizeChange,
          accessibilityLabelPrefix: 'Ventas por página',
        }}
      />
    </View>
  );
}

const COL_DATE = 72;
const COL_AMOUNT = 96;
const COL_COLLECT = 68;
const COL_CHEVRON = 20;

const styles = StyleSheet.create({
  section: {
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  sectionHeading: {
    gap: 2,
  },
  sectionSummary: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  sectionSummaryText: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
  },
  sectionSummaryDot: {
    ...typography.caption,
    color: ticketColors.ink3,
  },
  sectionSummaryAmount: {
    ...typography.caption,
    color: ticketColors.gold,
    fontWeight: '700',
  },
  filterPanel: {
    ...ticketSurfaces.paperSheet,
    backgroundColor: ticketColors.paper2,
    padding: spacing.sm,
  },
  tableHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    gap: spacing.xs,
  },
  tableHeaderCell: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    fontSize: 10,
  },
  dataRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    gap: spacing.xs,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
  },
  rowMainPressable: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: spacing.xs,
    minWidth: 0,
  },
  dataRowLast: {
    borderBottomWidth: 0,
  },
  colDate: {
    width: COL_DATE,
    flexShrink: 0,
  },
  colTicket: {
    flex: 1,
    minWidth: 80,
  },
  colAmount: {
    width: COL_AMOUNT,
    flexShrink: 0,
    alignItems: 'flex-end',
  },
  colCollect: {
    width: COL_COLLECT,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  collectBtn: {
    width: COL_COLLECT,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.xs,
    borderRadius: 10,
    backgroundColor: ticketColors.paper,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    minHeight: 44,
  },
  collectBtnText: {
    ...typography.caption,
    color: ticketColors.ink,
    fontWeight: '700',
    fontSize: 11,
  },
  colChevron: {
    width: COL_CHEVRON,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cellDate: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
  },
  ticketCell: {
    gap: 1,
  },
  ticketId: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
    fontVariant: ['tabular-nums'],
  },
  ticketMeta: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontSize: 11,
  },
  amountCell: {
    gap: 4,
    alignItems: 'flex-end',
  },
  pendingAmount: {
    ...typography.body,
    color: ticketColors.gold,
    fontWeight: '700',
    fontVariant: ['tabular-nums'],
  },
  pendingAmountPaid: {
    color: ticketColors.ok,
  },
  progressTrack: {
    width: '100%',
    height: 2,
    marginTop: 2,
    backgroundColor: ticketColors.rule,
    borderRadius: 1,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: ticketColors.gold,
  },
});
